import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import serverService from '../services/serverService';

/**
 * 프로젝트 목록 페이지
 * 모든 ilouli 서브도메인 프로젝트의 현황을 한눈에 표시
 */

const projects = [
  { key: 'main', name: 'Main', icon: '🏠', subdomain: 'ilouli.com', pm2: 'ilouli-main-api', tier: 'general', description: '메인 플랫폼 (랜딩, 인증, 포털)' },
  { key: 'auth', name: 'Auth', icon: '🔐', subdomain: 'auth.ilouli.com', pm2: 'ilouli-auth-api', tier: 'admin', description: '중앙 인증 서비스' },
  { key: 'community', name: 'Community', icon: '💬', subdomain: 'community.ilouli.com', pm2: 'ilouli-community-api', tier: 'general', description: '커뮤니티 + 트렌드 분석' },
  { key: 'stock', name: 'Stock', icon: '📈', subdomain: 'stock.ilouli.com', pm2: 'ilouli-stock', tier: 'subscriber', description: '주식 정보 및 차트' },
  { key: 'ai', name: 'AI', icon: '🤖', subdomain: 'ai.ilouli.com', pm2: 'ilouli-ai-api', tier: 'subscriber', description: 'AI 기반 콘텐츠 도구' },
  { key: 'family', name: 'Family', icon: '👨‍👩‍👧‍👦', subdomain: 'family.ilouli.com', pm2: 'ilouli-family-api', tier: 'family', description: '가족 전용 공간' },
  { key: 'lab', name: 'Lab', icon: '🔬', subdomain: 'lab.ilouli.com', pm2: 'ilouli-lab-api', tier: 'family', description: '실험/테스트/게임/파일관리 공간' },
  { key: 'devteam', name: 'DevTeam', icon: '👨‍💻', subdomain: 'devteam.ilouli.com', pm2: 'ilouli-devteam', tier: 'admin', description: 'AI 개발팀 운영 시스템' },
  { key: 'work', name: 'Work', icon: '🎬', subdomain: 'work.ilouli.com', pm2: 'ilouli-work-backend', tier: 'general', description: '3D 애니메이션 협업' },
  { key: 'openclaude', name: 'Claude Terminal', icon: '🖥️', subdomain: 'claude.ilouli.com', pm2: 'ilouli-openclaude', tier: 'admin', description: 'Claude Code 웹 터미널' },
  { key: 'stockanalysis', name: 'Stock Analysis', icon: '📊', subdomain: 'stockanalysis.ilouli.com', pm2: 'ilouli-stockanalysis', tier: 'admin', description: '뉴스 연쇄효과 종목 분석' },
  { key: 'news', name: 'News Insight', icon: '📰', subdomain: 'news.ilouli.com', pm2: 'ilouli-news-api', tier: 'general', description: '뉴스 기반 주식시장 인사이트' },
  { key: 'markdown', name: 'Markdown Blog', icon: '📝', subdomain: 'markdown.ilouli.com', pm2: 'ilouli-markdownblog-api', tier: 'admin', description: 'Bear 스타일 마크다운 에디터' },
  { key: 'nexus', name: 'Nexus', icon: '🕸️', subdomain: 'nexus.ilouli.com', pm2: 'ilouli-nexus', tier: 'admin', description: '지식 그래프 플랫폼' },
  { key: 'invest', name: 'Invest', icon: '💹', subdomain: 'invest.ilouli.com', pm2: 'ilouli-invest-api', tier: 'subscriber', description: '국내+해외 통합 투자 분석' },
];

const tierColors = {
  general: { bg: 'bg-gray-100', text: 'text-gray-600' },
  subscriber: { bg: 'bg-blue-100', text: 'text-blue-600' },
  family: { bg: 'bg-purple-100', text: 'text-purple-600' },
  admin: { bg: 'bg-red-100', text: 'text-red-600' },
};

const Projects = () => {
  const [pm2Processes, setPm2Processes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const loadProcesses = async () => {
      try {
        const result = await serverService.getPM2Processes();
        if (result.success) {
          setPm2Processes(result.data || []);
        }
      } catch (err) {
        console.error('Failed to load PM2 processes:', err);
      }
      setLoading(false);
    };
    loadProcesses();
  }, []);

  const getStatus = (pm2Name) => {
    const proc = pm2Processes.find(p => p.name === pm2Name);
    if (!proc) return { status: 'unknown', uptime: null, memory: null, cpu: null, restarts: null };
    return {
      status: proc.status === 'online' ? 'online' : 'offline',
      uptime: proc.uptime,
      memory: proc.memory ? `${Math.round(proc.memory / 1024 / 1024)}MB` : null,
      cpu: proc.cpu != null ? `${proc.cpu}%` : null,
      restarts: proc.restarts,
    };
  };

  const filtered = filter === 'all'
    ? projects
    : filter === 'online'
      ? projects.filter(p => getStatus(p.pm2).status === 'online')
      : filter === 'offline'
        ? projects.filter(p => getStatus(p.pm2).status !== 'online')
        : projects.filter(p => p.tier === filter);

  const onlineCount = projects.filter(p => getStatus(p.pm2).status === 'online').length;
  const offlineCount = projects.filter(p => getStatus(p.pm2).status !== 'online').length;

  return (
    <div className="flex flex-col gap-6">
      {/* 헤더 */}
      <div className="flex justify-between items-center max-md:flex-col max-md:gap-3 max-md:items-start">
        <div>
          <h1 className="text-2xl font-bold text-[#1d1d1f] m-0">프로젝트 관리</h1>
          <p className="text-sm text-gray-500 mt-1">ilouli.com 서브도메인 서비스 현황</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
            {onlineCount}개 정상
          </span>
          {offlineCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
              {offlineCount}개 오프라인
            </span>
          )}
        </div>
      </div>

      {/* 필터 */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: 'all', label: `전체 (${projects.length})` },
          { value: 'online', label: `정상 (${onlineCount})` },
          { value: 'general', label: 'General' },
          { value: 'subscriber', label: 'Subscriber' },
          { value: 'family', label: 'Family' },
          { value: 'admin', label: 'Admin' },
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(filter === f.value ? 'all' : f.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer ${
              filter === f.value
                ? 'bg-[#3B82F6] text-white border-[#2563EB]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* 프로젝트 그리드 */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">로딩 중...</div>
      ) : (
        <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-2 max-md:grid-cols-1">
          {filtered.map(project => {
            const info = getStatus(project.pm2);
            const tc = tierColors[project.tier] || tierColors.general;

            return (
              <Link
                key={project.key}
                to={`/projects/${project.key}`}
                className="flex flex-col bg-white border border-gray-200 rounded-xl p-5 no-underline transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-gray-300"
              >
                {/* 상단: 아이콘 + 이름 + 상태 */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl leading-none">{project.icon}</span>
                    <div>
                      <span className="text-[15px] font-semibold text-[#1d1d1f] block">{project.name}</span>
                      <span className="text-[11px] text-gray-400 font-mono">{project.subdomain}</span>
                    </div>
                  </div>
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                    info.status === 'online' ? 'bg-green-500' : info.status === 'offline' ? 'bg-red-500' : 'bg-gray-300'
                  }`}></span>
                </div>

                {/* 설명 */}
                <p className="text-[13px] text-gray-500 mb-3 leading-relaxed">{project.description}</p>

                {/* 하단: 메트릭 + 티어 */}
                <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-3 text-[11px] text-gray-400">
                    {info.memory && <span>{info.memory}</span>}
                    {info.uptime && <span>{info.uptime}</span>}
                    {info.restarts != null && info.restarts > 0 && (
                      <span className="text-amber-500">↻{info.restarts}</span>
                    )}
                  </div>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${tc.bg} ${tc.text}`}>
                    {project.tier}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Projects;
